import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useActiveOrg } from '../../active-org.ts';
import { useAuth } from '../../api.ts';
import { LinkableRepos } from '../../components/LinkableRepos.tsx';

export interface EmptyStateCta {
  label: string;
  /** In-app route — rendered as a client-side link when set… */
  href?: string;
  /** …otherwise as a button. */
  onClick?: () => void;
}

/** Shared CTA for "no ingested data" empties — routes to data-source settings. */
export const CONNECT_REPO_CTA: EmptyStateCta = {
  label: 'Connect a Repo',
  href: '/setup/integrations',
};

interface EmptyStateProps {
  /** Primary headline (the "H1" copy slot). Omit for a copy-less box. */
  title?: string;
  /** Secondary line (the "H2" copy slot). */
  description?: ReactNode;
  /** Optional call-to-action rendered at the bottom of the box. */
  cta?: EmptyStateCta;
  /** Tighter padding + smaller type for narrow areas (e.g. the sidebar). */
  compact?: boolean;
  /** Extra class for placement (e.g. spanning a grid's columns). */
  className?: string;
  /** Optional content rendered inside the box, between the copy and CTA. */
  children?: ReactNode;
}

/**
 * Shared empty-state surface: a dashed box with centered copy and an
 * optional CTA at the bottom. One look for every "nothing here yet" moment
 * across the app (snapshot views, roadmap sections, accounting matrices).
 *
 * `title` is rendered as a `<p>` (not a heading) so it never competes with a
 * page-level `<h1>`; the styling gives it the visual weight of a headline.
 */
export function EmptyState({
  title,
  description,
  cta,
  compact = false,
  className,
  children,
}: EmptyStateProps) {
  const classes = [
    'snap-empty',
    compact ? 'snap-empty--compact' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      {title ? <p className='snap-empty__title'>{title}</p> : null}
      {description ? <p className='snap-empty__desc'>{description}</p> : null}
      {children ? <div className='snap-empty__body'>{children}</div> : null}
      {cta ? (
        cta.href ? (
          <Link className='btn snap-empty__cta' to={cta.href}>
            {cta.label}
          </Link>
        ) : (
          <button type='button' className='btn snap-empty__cta' onClick={cta.onClick}>
            {cta.label}
          </button>
        )
      ) : null}
    </div>
  );
}

/**
 * "No ingested data yet" empty state. Beyond the dashed copy box it
 * surfaces the repos available to connect (so the user can link one
 * without a detour to settings) plus a secondary CTA to set up another
 * integration. Resolves the active org itself, so callers only pass a
 * title. Falls back to the plain Connect-a-Repo CTA when there's no
 * active org yet (e.g. signed-out / pre-provisioning).
 */
export function ConnectReposEmpty({ title }: { title: string }) {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);
  return (
    <EmptyState
      title={title}
      description='Select a repo to populate platform data.'
      cta={active ? undefined : CONNECT_REPO_CTA}
    >
      {active ? (
        <div className='stack'>
          <LinkableRepos orgSlug={active.slug} defaultOpen />
          <p style={{ marginTop: 'var(--3u)' }}>
            <Link to='/onboarding/connect-repo' className='snap-empty__secondary'>
              Connect another repo
            </Link>
          </p>
        </div>
      ) : null}
    </EmptyState>
  );
}
